import CrossIcon from "@assets/icons/cross.svg";
import SpinnerIcon from "@assets/icons/spinner.svg";

import { useI18n } from "@core/i18n/i18n";
import { healthcareTeamManager } from "@core/service-container";
import { sendAnalyticsEventHctLeft } from "@domain/healthcare-team/healthcare-team-analytics";
import { Patient } from "@domain/patient/patient";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { ToastContext } from "@ui/components/common/toast-provider";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { RoutesPath } from "@ui/navigation/routes";
import { physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { formatName } from "@utils/string-utils";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router";

export interface HealthcareTeamLeaveDialogProps extends DialogProps {
	patient: Patient;
}

const LEAVE_BUTTON_WIDTH = 160;

export const HealthcareTeamLeaveDialog = ({
	patient,
	onHide,
	...otherProps
}: HealthcareTeamLeaveDialogProps): JSX.Element => {
	const { format } = useI18n();
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();
	const { showToast } = useContext(ToastContext);

	const { isDesktop } = useDimensions();

	const patientFullName = useMemo(() => formatName(patient), [patient]);
	const leaveTeam = useCallback(async () => {
		setIsLoading(true);
		try {
			await healthcareTeamManager.leaveHealthcareTeam(patient.id).then(() => setIsLoading(false));
			onHide();
			sendAnalyticsEventHctLeft();
			navigate(RoutesPath.Home);
			showToast(
				format("patientDetails.healthcareTeam.leaveHealthcareTeamModal.successSummary"),
				format("patientDetails.healthcareTeam.leaveHealthcareTeamModal.successDetail", {
					patientName: patientFullName,
				}),
				"success",
			);
		} catch (error) {
			setIsLoading(false);
		}
	}, [format, navigate, onHide, patient.id, patientFullName, showToast]);

	return (
		<StyledDialog
			width={DIALOG_SIZE.S}
			header={format("patientDetails.healthcareTeam.leaveHealthcareTeamModal.title")}
			onHide={onHide}
			closeIcon={<CrossIcon width={24} height={24} color={colors.black} />}
			{...otherProps}
			footer={
				<FooterContainer>
					<Button
						label={format("patientDetails.healthcareTeam.leaveHealthcareTeamModal.stayInTeamButton")}
						uiStyle="secondary_B"
						onClick={onHide}
						style={{ width: isDesktop ? "" : "100%" }}
					/>
					<Button
						label={isLoading ? "" : format("patientDetails.healthcareTeam.leaveHealthcareTeamModal.leaveTeamButton")}
						uiStyle="danger"
						icon={isLoading ? <SpinnerIcon /> : undefined}
						onClick={leaveTeam}
						style={{ width: isDesktop ? LEAVE_BUTTON_WIDTH : "100%" }}
					/>
				</FooterContainer>
			}
		>
			<ContentContainer>
				<p>
					{format("patientDetails.healthcareTeam.leaveHealthcareTeamModal.leavePatientName", {
						patientName: patientFullName,
					})}
				</p>
				<p>{format("patientDetails.healthcareTeam.leaveHealthcareTeamModal.wontBeAbleToAccess")}</p>
				<p>{format("patientDetails.healthcareTeam.leaveHealthcareTeamModal.patientAndHCTNotified")}</p>
			</ContentContainer>
		</StyledDialog>
	);
};

const StyledDialog = styled(BaseDialog)`
	.p-dialog-footer {
		padding: 8px 32px 32px;
	}
`;

const ContentContainer = styled.div`
	${stack(24)}
`;

const FooterContainer = styled.div`
	${row(16, "flexend")}
	@media ${physioMeMobileBreakpoint} {
		${stack(8, "center", "center")}
		flex-direction: column-reverse;
	}
`;
